import { CommonModule } from '@angular/common';
import { Component, computed, inject, Inject, signal } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { ComponentsModule } from '@components/components.module';
import { CoreModule } from '@core/core.module';
import { removeIllegalCharactersFromFileName } from '@core/util/documents';
import { createId } from '@core/util/file';
import {
	DOCUMENT_REQUEST_DOCUMENT_STATUS,
	DOCUMENT_REQUEST_INTERNAL_STATUS,
	DOCUMENT_REQUEST_STATUS,
	DocumentRequestStatusHistory,
} from '@models/document-requests';
import { MatterFileType } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { DocumentRequestsRepo } from '@repos/document-requests/document-requests.repo';
import { UserProfileState } from '@state/user-profile';
import { finalize, filter, first } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { AtomicModule } from 'src/app/shared/atomic/atomic.module';
import * as DocumentRequestsActions from 'src/app/shared/state/document-requests/document-requests.actions';
import { ContentFooterComponent } from 'src/app/ui/content-footer/content-footer.component';
import { DRAWER_DATA } from 'src/app/ui/drawer/drawer-data.token';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { ToastService } from 'src/app/ui/toast/toast.service';
import { UiModule } from '../../../../../ui/ui.module';

interface UploadError {
	code: string;
	message: string;
}

@UntilDestroy()
@Component({
	selector: 'app-request-form',
	templateUrl: './request-form.component.html',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FormsModule,
		ComponentsModule,
		UiModule,
		CoreModule,
		AtomicModule,
		ContentFooterComponent,
		IconsModule,
	],
})
export class RequestFormComponent {
	store = inject(Store);
	storage = inject(AngularFireStorage);
	toastService = inject(ToastService);
	documentRequestsRepo = inject(DocumentRequestsRepo);
	fb = inject(FormBuilder);

	businessId = this.store.selectSnapshot(UserProfileState.getCurrentBusinessId);
	userId = this.store.selectSnapshot(UserProfileState.getProfileId);

	uploadForm: FormGroup;
	uploadedFiles: Array<{
		file: File;
		type: MatterFileType[];
		docId: string;
		expanded: boolean;
	}> = [];
	isUploading = signal(false);
	isSubmitting = signal(false);

	constructor(@Inject(DRAWER_DATA) private data: { closeDrawer: () => void }) {
		this.uploadForm = this.fb.group({
			note: [''],
		});
	}

	onFileSelected(files: File[]) {
		for (const file of files) {
			const docId = createId();
			this.uploadedFiles.push({
				file,
				type: [MatterFileType.PURCHASE],
				docId,
				expanded: false,
			});

			this.uploadForm.addControl(
				`displayName_${this.uploadedFiles.length - 1}`,
				this.fb.control(''),
			);
			this.uploadForm.addControl(
				`note_${this.uploadedFiles.length - 1}`,
				this.fb.control(''),
			);
		}
	}

	formatFileSize(size: number): string {
		if (size < 1024) {
			return size + ' bytes';
		} else if (size < 1024 * 1024) {
			return (size / 1024).toFixed(1) + ' KB';
		} else {
			return (size / (1024 * 1024)).toFixed(1) + ' MB';
		}
	}

	removeFile(index: number) {
		this.uploadForm.removeControl(`displayName_${index}`);
		this.uploadForm.removeControl(`note_${index}`);

		this.uploadedFiles.splice(index, 1);

		for (let i = index; i < this.uploadedFiles.length; i++) {
			const displayNameValue = this.uploadForm.get(
				`displayName_${i + 1}`,
			).value;
			const noteValue = this.uploadForm.get(`note_${i + 1}`).value;

			this.uploadForm.removeControl(`displayName_${i + 1}`);
			this.uploadForm.removeControl(`note_${i + 1}`);

			this.uploadForm.addControl(
				`displayName_${i}`,
				this.fb.control(displayNameValue),
			);
			this.uploadForm.addControl(`note_${i}`, this.fb.control(noteValue));
		}
	}

	toggleFileExpanded(index: number) {
		this.uploadedFiles[index].expanded = !this.uploadedFiles[index].expanded;
	}

	closeDrawer() {
		this.data.closeDrawer();
	}

	primaryAction = computed(() => ({
		text: this.isSubmitting() ? 'Submitting...' : 'Submit',
		action: () => this.onSubmit(),
		disabled: this.isSubmitting(),
	}));

	secondaryAction = computed(() => ({
		text: 'Cancel',
		action: () => this.closeDrawer(),
		disabled: this.isSubmitting(),
	}));

	async onSubmit() {
		if (!this.uploadForm.valid || this.uploadedFiles.length === 0) {
			this.toastService.error(
				'Please add at least one file before submitting a request.',
			);
			return;
		}

		this.isSubmitting.set(true);

		const requestId = createId();

		// Create the request document first
		const statusHistory: DocumentRequestStatusHistory[] = [
			{
				status: DOCUMENT_REQUEST_STATUS.PENDING,
				timestamp: Date.now(),
				updatedBy: this.userId,
				isInternal: false,
			},
		];

		try {
			// Create the request document first
			this.store.dispatch(
				new DocumentRequestsActions.Create({
					id: requestId,
					createdAt: Date.now(),
					updatedAt: Date.now(),
					createdBy: this.userId,
					updatedBy: this.userId,
					businessId: this.businessId,
					requestType: 'import-documents',
					status: DOCUMENT_REQUEST_STATUS.PENDING,
					assignedTo: null,
					note: this.uploadForm.get('note').value,
					documentsCount: this.uploadedFiles.length,
					statusHistory,
					internalStatus: DOCUMENT_REQUEST_INTERNAL_STATUS.PENDING,
				}),
			);

			// Wait for request creation to complete
			await firstValueFrom(
				this.store
					.select((state) => state.documentRequests.requestCreationInProgress)
					.pipe(
						filter((inProgress) => !inProgress),
						first(),
						untilDestroyed(this),
					),
			);

			// Now upload the files
			this.isUploading.set(true);
			const uploadPromises = [];

			for (let i = 0; i < this.uploadedFiles.length; i++) {
				const fileData = this.uploadedFiles[i];
				const file = fileData.file;

				const fileNameSections = file.name.split('.');
				const fileName =
					removeIllegalCharactersFromFileName(fileNameSections[0]).replace(
						/\s/g,
						'_',
					) +
					'.' +
					fileNameSections[1];

				const filePath = `documentRequests/${this.businessId}/${requestId}/${fileName}`;
				const task = this.storage.upload(filePath, file);

				const uploadPromise = new Promise<void>((resolve, reject) => {
					task
						.snapshotChanges()
						.pipe(
							untilDestroyed(this),
							finalize(async () => {
								const displayName = this.uploadForm.get(
									`displayName_${i}`,
								).value;
								const note = this.uploadForm.get(`note_${i}`).value;
								const statusHistory: DocumentRequestStatusHistory[] = [
									{
										status: DOCUMENT_REQUEST_STATUS.PENDING,
										timestamp: Date.now(),
										updatedBy: this.userId,
										isInternal: false,
									},
								];

								await this.documentRequestsRepo.addDocumentToRequest(
									requestId,
									{
										name: fileName,
										size: file.size,
										contentType: file.type,
										storagePath: filePath,
										status: DOCUMENT_REQUEST_DOCUMENT_STATUS.PENDING,
										displayName: displayName,
										note: note,
										statusHistory,
										googleDriveLink: '',
									},
								);
								resolve();
							}),
						)
						.subscribe({
							error: (error: UploadError) => {
								console.error('Upload failed:', error);
								reject(error);
							},
						});
				});

				uploadPromises.push(uploadPromise);
			}

			await Promise.all(uploadPromises);

			this.toastService.success('Your request has been submitted successfully');

			this.uploadForm.reset({
				contextType: 'documents',
			});
			this.uploadedFiles = [];
		} catch (error: unknown) {
			const errorMessage =
				error instanceof Error ? error.message : 'Unknown error';
			this.toastService.error(`Error submitting request: ${errorMessage}`);
		} finally {
			this.isUploading.set(false);
			this.isSubmitting.set(false);
			this.data.closeDrawer();
		}
	}
}
